import AvatarDefaultIcon from '@assets/images/avatar/default.png';
import Logo from '@assets/images/common/brand/logo-primary.svg';
import BlankTabIcon from '@assets/images/common/icon/blank-tab/blank-tab.svg';
import { useTenantAuth } from '@src/context/auth/useTenantAuth';
import { useUnsavedInviteUsers } from '@src/context/UnsavedInviteUsersContext';
import { SHARED_URL } from '@src/routes/shared';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button';
import UserGuideLink from '@src/ui/elements/button/UserGuideLink';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ConfirmMemberInvitationLeaveModal } from './ConfirmMemberInvitationLeaveModal';

const CommunityMobileHeaderForTenant = () => {
  const navigate = useNavigate();
  const tenantAuth = useTenantAuth();
  const { unsavedInviteUsers, setUnsavedInviteUsers } = useUnsavedInviteUsers();
  const [isOpen, setIsOpen] = useState(false);
  const [isFeatureListOpen, setIsFeatureListOpen] = useState(false);
  const [isManagementListOpen, setIsManagementListOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const handleExternalLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string | undefined,
  ) => {
    if (!url) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const menuLinks = [
    {
      title: 'マイプロフィール',
      link: TENANT_URL.PROFILE,
      targetBlank: true,
    },
    {
      title: 'ログアウト',
      link: TENANT_URL.LOGOUT,
      targetBlank: false,
    },
  ];

  const handleMenu = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setIsOpen(!isOpen);
    setIsFeatureListOpen(false);
    setIsManagementListOpen(false);
    toggle(setter);
  };

  const toggle = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setter((prevState) => !prevState);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    if (unsavedInviteUsers.length === 0) {
      return;
    }
    e.preventDefault();
    setPendingNavigation(to);
    setIsModalOpen(true);
  };

  const confirmReturn = () => {
    setIsModalOpen(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
      setUnsavedInviteUsers([]);
    }
  };

  const cancelReturn = () => {
    setPendingNavigation(null);
    setIsModalOpen(false);
  };

  return (
    <>
      <header className='fixed top-0 z-[9999] hidden w-full items-center justify-between border-b border-solid border-b-gray-200 bg-white lg:flex'>
        {!isOpen && (
          <button
            className='relative z-50 ml-[10px] flex h-[60px] w-[45px] items-center justify-center'
            onClick={() => handleMenu(setIsFeatureListOpen)}
            aria-expanded={isOpen}
            aria-label='メニュー'
          >
            <span
              className={`absolute inset-0 m-auto h-[2px] w-[18px] translate-y-[-5px] rounded-md bg-primary transition-all`}
            />
            <span
              className={`absolute inset-0 m-auto h-[2px] w-[18px] rounded-md bg-primary transition-all `}
            />
            <span
              className={`absolute inset-0 m-auto h-[2px] w-[18px] translate-y-[5px] rounded-md bg-primary transition-all`}
            />
          </button>
        )}
        <Link
          to={TENANT_URL.HOME}
          className={`block ${isOpen && 'px-[24px]'}`}
          onClick={(e) => handleLogoClick(e, TENANT_URL.HOME)}
        >
          <img
            src={Logo}
            alt=''
            className='block h-auto max-h-[40px] w-[220px] object-contain'
          />
        </Link>
        <button
          className='relative z-50 flex size-[60px] items-center justify-center'
          onClick={() => handleMenu(setIsManagementListOpen)}
          aria-expanded={isOpen}
          aria-label='メニュー'
        >
          <span
            className={`absolute inset-0 m-auto size-[4px] translate-y-[-6px] rounded-md bg-primary transition-all ${
              isOpen ? 'h-[2px] w-[18px] !translate-y-0 rotate-45' : ''
            }`}
          />
          <span
            className={`absolute inset-0 m-auto size-[4px] rounded-md bg-primary transition-all ${
              isOpen ? 'h-[2px] w-[18px] !translate-x-1 opacity-0' : ''
            }`}
          />
          <span
            className={`absolute inset-0 m-auto size-[4px] translate-y-[6px] rounded-md bg-primary transition-all ${
              isOpen ? 'h-[2px] w-[18px] !translate-y-0 -rotate-45' : ''
            }`}
          />
        </button>
      </header>
      {/* ドロワーメニュー */}
      <div
        className={`fixed inset-x-0 top-[60px] z-[9998] h-[calc(100%-60px)] bg-white transition-all duration-200 ${
          isOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-2 opacity-0'
        }`}
      >
        <nav className='flex h-full flex-col px-6 py-5'>
          {isFeatureListOpen && (
            <ul className='flex flex-col'>
              <li className={`relative flex-[0_0_auto] border-white`}>
                <a
                  href={tenantAuth.profile?.topPageLink}
                  target='_blank'
                  className='flex items-center gap-[2px] px-2 py-[18px]'
                >
                  ポータル管理
                  <img
                    src={BlankTabIcon}
                    alt=''
                    width='16'
                    height='16'
                    className='size-4'
                  />
                </a>
              </li>
              <li
                className={`relative flex-[0_0_auto] border-b-2 border-primary font-semibold text-primary`}
              >
                <a
                  href={SHARED_URL.COMMUNITY_DEFAULT_CHANNEL}
                  className='flex items-center px-2 py-[18px]'
                >
                  オンラインコミュニティ
                </a>
              </li>
            </ul>
          )}
          {isManagementListOpen && tenantAuth.profile && (
            <>
              <div className='mt-5 flex flex-col gap-[14px]'>
                <div className='flex items-center gap-4 px-3'>
                  <img
                    className='size-6 rounded-full object-cover'
                    src={tenantAuth.profile.user?.avatarUrl || AvatarDefaultIcon}
                    alt=''
                    width='24'
                    height='24'
                  />
                  <p className='grow truncate'>
                    <span className='block truncate text-sm font-semibold'>
                      {`${tenantAuth.profile.user?.familyName || ''} ${tenantAuth.profile.user?.firstName || ''}`}
                    </span>
                  </p>
                </div>
                <div className='flex flex-col gap-px'>
                  {menuLinks.map((link) => (
                    <React.Fragment key={link.link}>
                      {' '}
                      {link.link === TENANT_URL.LOGOUT ? (
                        <div className='mt-7 flex flex-col gap-[20px]'>
                          <Button
                            variant='quaternary'
                            outline
                            onClick={() => navigate(link.link)}
                          >
                            ログアウト
                          </Button>
                        </div>
                      ) : (
                        <a
                          href={link.link}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='group flex cursor-pointer items-center gap-2 px-3 py-[20px] transition-colors'
                          onClick={(e) => handleExternalLinkClick(e, link.link)}
                        >
                          {link.title}
                          <img
                            src={BlankTabIcon}
                            alt=''
                            width='16'
                            height='16'
                            className='block size-4'
                          />
                        </a>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <UserGuideLink
                userType='tenant'
                device='mobile'
                color='gray'
              />
            </>
          )}
        </nav>
        <ConfirmMemberInvitationLeaveModal
          isOpen={isModalOpen}
          onConfirm={confirmReturn}
          onClose={cancelReturn}
        />
      </div>
    </>
  );
};

export default CommunityMobileHeaderForTenant;
